// Utility helpers to compute emissions and display amounts
export function computeEmissionKg({
  request = {},
  googleApiResponse = {},
  response = {},
} = {}) {
  // 1) direct totalEmission (kg) from googleApiResponse or fallback response
  if (
    typeof googleApiResponse.totalEmission === "number" &&
    googleApiResponse.totalEmission > 0
  ) {
    return googleApiResponse.totalEmission;
  }
  if (
    typeof response.totalEmission === "number" &&
    response.totalEmission > 0
  ) {
    return response.totalEmission;
  }

  // 2) flightEmissions array with per-flight totalEmission
  if (
    Array.isArray(googleApiResponse.flightEmissions) &&
    googleApiResponse.flightEmissions.length
  ) {
    const sumTotal = googleApiResponse.flightEmissions.reduce(
      (sum, f) =>
        sum + (typeof f?.totalEmission === "number" ? f.totalEmission : 0),
      0
    );
    if (sumTotal > 0) return sumTotal;

    // 3) flightEmissions entries with emissionsGramsPerPax (grams per passenger)
    const sumGramsPerPax = googleApiResponse.flightEmissions.reduce(
      (sum, f) => {
        const eg = f?.emissionsGramsPerPax;
        if (!eg) return sum;
        if (typeof eg === "number") return sum + eg;

        // eg is an object like { first, business, premiumEconomy, economy }
        const cabinKeyMap = {
          first: "first",
          business: "business",
          premiumEconomy: "premiumEconomy",
          economy: "economy",
        };
        const cabinKey =
          (request && request.cabinClass && cabinKeyMap[request.cabinClass]) ||
          "economy";
        if (typeof eg[cabinKey] === "number") return sum + eg[cabinKey];

        const nums = Object.values(eg).filter((v) => typeof v === "number");
        if (nums.length) {
          const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
          return sum + avg;
        }

        return sum;
      },
      0
    );

    if (sumGramsPerPax > 0) {
      const pax = (request && request.passengers) || 1;
      return (sumGramsPerPax * pax) / 1000; // grams -> kg
    }
  }

  // 4) other common fields
  if (
    typeof googleApiResponse.co2eKg === "number" &&
    googleApiResponse.co2eKg > 0
  )
    return googleApiResponse.co2eKg;
  if (typeof googleApiResponse.co2e === "number" && googleApiResponse.co2e > 0)
    return googleApiResponse.co2e;

  // 5) fallback: use response.emissionPerPassenger * passengers
  if (
    typeof response.emissionPerPassenger === "number" &&
    response.emissionPerPassenger > 0
  ) {
    const pax = (request && request.passengers) || 1;
    return response.emissionPerPassenger * pax;
  }

  return 0;
}

export function computeDisplayAmount(
  { request = {}, googleApiResponse = {}, response = {}, paymentId } = {},
  ratePerTon = 20
) {
  const emissionKg = computeEmissionKg({
    request,
    googleApiResponse,
    response,
  });
  const computedAmount =
    emissionKg > 0 ? (emissionKg / 1000) * ratePerTon : null;
  const amountFloat =
    computedAmount && computedAmount > 0 ? computedAmount : 25;

  // If a payment record exists with an amount, prefer that (assumed in USD)
  if (
    paymentId &&
    typeof paymentId.amount === "number" &&
    paymentId.amount > 0
  ) {
    return Math.trunc(paymentId.amount);
  }

  // Remove decimal part (truncate) as requested
  return Math.trunc(amountFloat);
}

export default { computeEmissionKg, computeDisplayAmount };
